import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Megaphone } from 'lucide-react';
import { taskInputSchema, type TaskInput } from '@underline/shared';
import { BottomSheet } from '@/components/ui/bottom-sheet';
import { FloatingField, FloatingTextarea } from '@/components/ui/floating-field';
import { FieldShell } from '@/components/ui/stacked-field';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { useCreateTask, useUpdateTask } from '@/hooks/use-tasks';
import { useUsers } from '@/hooks/use-users';
import { useUiStore } from '@/stores/ui-store';
import { todayISO } from '@/lib/dates';

/* ── Tipografia MONO 12px — hierarquia só por peso/cor ──────────────── */
const TITLE_MONO = 'font-mono text-[12px] font-semibold uppercase tracking-wider text-foreground';
const SECTION_MONO = 'font-mono text-[12px] uppercase tracking-wider text-muted-foreground';
const FIELD_MONO = 'mono uppercase tabular-nums text-[12px]';
const BTN_MONO = 'font-mono text-[12px] uppercase tracking-wider';

export function TaskFormModal() {
  const open = useUiStore((s) => s.taskFormOpen);
  const close = useUiStore((s) => s.closeTaskForm);
  const editing = useUiStore((s) => s.editingTask);
  const isEdit = !!editing;

  const { data: users = [] } = useUsers();
  const create = useCreateTask();
  const update = useUpdateTask();

  const form = useForm<TaskInput>({
    resolver: zodResolver(taskInputSchema),
    defaultValues: editing
      ? {
          event_id: editing.event_id,
          brand_id: editing.brand_id,
          title: editing.title,
          responsible_id: editing.responsible_id,
          due_date: editing.due_date,
          due_time: editing.due_time,
          description: editing.description,
          status: editing.status,
        }
      : {
          event_id: null,
          brand_id: 1,
          title: '',
          responsible_id: users[0]?.id ?? 0,
          due_date: todayISO(),
          due_time: null,
          description: null,
          status: 'pending',
        },
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = form;

  const responsibleId = watch('responsible_id');

  useEffect(() => {
    if (!open) return;
    if (editing) {
      reset({
        event_id: editing.event_id,
        brand_id: editing.brand_id,
        title: editing.title,
        responsible_id: editing.responsible_id,
        due_date: editing.due_date,
        due_time: editing.due_time,
        description: editing.description,
        status: editing.status,
      });
    } else {
      reset({
        event_id: null,
        brand_id: 1,
        title: '',
        responsible_id: users[0]?.id ?? 0,
        due_date: todayISO(),
        due_time: null,
        description: null,
        status: 'pending',
      });
    }
  }, [open, editing, users, reset]);

  async function onSubmit(values: TaskInput) {
    try {
      if (isEdit && editing) {
        await update.mutateAsync({ id: editing.id, input: values });
        toast.success('Post atualizado');
      } else {
        await create.mutateAsync(values);
        toast.success('Post criado');
      }
      close();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao salvar';
      toast.error(msg);
    }
  }

  const activeUsers = users.filter((u) => u.is_active);

  return (
    <BottomSheet
      open={open}
      onClose={close}
      title={isEdit ? 'Editar post' : 'Nova postagem'}
      titleClassName={TITLE_MONO}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <FloatingField
          label="Título do post"
          error={errors.title?.message}
          className={FIELD_MONO}
          {...register('title')}
        />

        <div>
          <p className={`${SECTION_MONO} mb-2`}>Responsável</p>
          <SegmentedControl
            value={responsibleId ?? activeUsers[0]?.id ?? 0}
            onChange={(v) =>
              setValue('responsible_id', Number(v), { shouldValidate: true, shouldDirty: true })
            }
            options={activeUsers.map((u) => ({
              value: u.id,
              label: u.name.split(' ')[0] ?? u.name,
            }))}
            size="sm"
            className="font-mono uppercase"
          />
          {errors.responsible_id && (
            <p className="mt-1 font-mono text-[12px] uppercase tracking-wider text-destructive">
              {errors.responsible_id.message}
            </p>
          )}
        </div>

        <FieldShell label="Data do post" error={errors.due_date?.message}>
          <Controller
            control={control}
            name="due_date"
            render={({ field }) => <DatePicker value={field.value} onChange={field.onChange} />}
          />
        </FieldShell>

        <FieldShell label="Horário">
          <Controller
            control={control}
            name="due_time"
            render={({ field }) => (
              <TimePicker value={field.value} onChange={field.onChange} />
            )}
          />
        </FieldShell>

        <FloatingTextarea
          label="Briefing: conceito, copy, hashtags…"
          className={FIELD_MONO}
          {...register('description', { setValueAs: (v: string) => v || null })}
        />

        <div className="flex items-center gap-2 pt-2">
          <button
            type="button"
            onClick={close}
            className={`flex-1 rounded-lg border border-border bg-card px-4 py-3 hover:bg-muted ${BTN_MONO} font-medium`}
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className={`flex-[2] flex items-center justify-center gap-2 rounded-lg bg-primary px-4 py-3 font-semibold text-primary-foreground shadow-red transition active:scale-[0.97] disabled:opacity-60 ${BTN_MONO}`}
          >
            <Megaphone className="h-4 w-4" />
            {isSubmitting ? 'Salvando...' : isEdit ? 'Salvar post' : 'Criar post'}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
}
